import { createMovementApi, deleteMovementApi, getMovementApi, getMovementDraftsApi, getMovementsApi, postMovementApi, removeDocumentMovementApi, updateMovementApi } from '@app/api/movement.api';
import { MovementDataArray, MovementItem, MovementQueryParams, MovementModel } from '@app/interfaces/movement';
import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import { showError } from '@app/utils/message';

export interface MovementState {
  data: MovementModel[];
  totalRows: number;
  pagination: {
    current: number,
    pageSize: number
  },
  filters: any,
  sorter: any,
  searchTerm: string;
  selectedRecord: MovementItem[];
  loading: boolean;
  error: string | null;
  draft: {
    data: MovementModel[];
    totalRows: number;
    pagination: {
      current: number,
      pageSize: number
    }
  },
  record: MovementModel | null;
}

const initialState: MovementState = {
  data: [],
  totalRows: 0,
  pagination: { current: 1, pageSize: 20 },
  filters: {},
  sorter: {},
  searchTerm: "",
  selectedRecord: [],
  loading: false,
  error: null,
  draft: {
    data: [],
    totalRows: 0,
    pagination: { current: 1, pageSize: 20 },
  },
  record: null,
};

interface ResponseData {
  data: MovementItem;
  message: string;
}

export const getInventMovements = createAsyncThunk<MovementDataArray, MovementQueryParams>(
  'movement/getInventMovements',
  async (params: MovementQueryParams, { rejectWithValue }) => {
    try {
      const customers = (await getMovementsApi(params)) as MovementDataArray;
      return customers || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const getInventMovement = createAsyncThunk<MovementModel, number>(
  'movement/getInventMovement',
  async (movementId, { rejectWithValue }) => {
    try {
      const data = await getMovementApi(movementId);
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred'
      );
    }
  }
);

export const createInventMovement = createAsyncThunk<ResponseData, MovementModel>(
  'movement/createInventMovement', async (data) => {
  const response = await createMovementApi(data, {
    headers: { 'Content-Type': 'multipart/form-data'}
  });
  return response;
});

export const updateInventMovement = createAsyncThunk<ResponseData, { movementId: number; formData: MovementModel }>(
  'movement/updateInventMovement',
  async ({ movementId, formData }) => {
    const response = await updateMovementApi(movementId, formData, {
      headers: { 'Content-Type': 'multipart/form-data'}
    });
    return response;
  },
);

export const deleteInventMovement = createAsyncThunk<{ movementId: number; response: any }, number>(
  'movement/deleteInventMovement',
  async (movementId, { rejectWithValue }) => {
    try {
      const response = await deleteMovementApi(movementId);
      return { movementId, response };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const postInventMovement = createAsyncThunk<ResponseData, number>(
  'movement/postInventMovement',
  async (movementId, { rejectWithValue }) => {
    try {
      const response = await postMovementApi(movementId);
      return response;
      
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const removeDocumentInventMovement = createAsyncThunk<ResponseData, {movementId: number, documentId: number}>(
  'movement/removeDocumentInventMovement',
  async ({movementId, documentId}, { rejectWithValue }) => {
    try {
      const response = await removeDocumentMovementApi(movementId, documentId);
      return response;
      
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const getInventMovementDrafts = createAsyncThunk<MovementDataArray, MovementQueryParams>(
  'movement/getInventMovementDrafts',
  async (params: MovementQueryParams, { rejectWithValue }) => {
    try {
      const customers = (await getMovementDraftsApi(params)) as MovementDataArray;
      return customers || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);


const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

// Slice
export const movementSlice = createSlice({
  name: 'movement',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      if (pagination !== undefined) state.pagination = pagination;
      if (filters !== undefined) state.filters = filters;
      if (sorter !== undefined) state.sorter = sorter;
      if (searchTerm !== undefined) state.searchTerm = searchTerm;
    },
    updateSearchTerm(state, action) {
      state.searchTerm = action.payload;
      state.pagination = { ...state.pagination, current: 1 };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInventMovements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInventMovements.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload.rows;
        state.data = data.map((item: MovementItem) => item.attributes) || [];

        state.totalRows = parseInt(action.payload.total_rows);
      })
      .addCase(getInventMovements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch movements';
      })
      .addCase(getInventMovement.fulfilled, (state, action) => {
        const movement = (action.payload as any).data;
        state.record = movement.attributes;
      })
      .addCase(createInventMovement.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.data.unshift(data.attributes);
        message.success(newMessage);
      })
      .addCase(updateInventMovement.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;

        const updatedIndex = state.data.findIndex((item) => Number(item.id) === Number(data.id));
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data.attributes;
          state.record = data.attributes;
          message.success(newMessage);
        }
      })
      .addCase(postInventMovement.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.record = data.attributes;
        message.success(newMessage);
      })
      .addCase(removeDocumentInventMovement.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;

        const updatedIndex = state.data.findIndex((item) => Number(item.id) === Number(data.id));
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data.attributes;
          state.record = data.attributes;
          message.success(newMessage);
        }
      })
      .addCase(deleteInventMovement.fulfilled, (state, action) => {
        const { movementId, response } = action.payload;
        state.data = state.data.filter((movement) => Number(movement.id) !== movementId);

        if (response.error) {
          showError(response.error);
        }
        else {
          message.success(response.message);
        }
      })
      .addCase(getInventMovementDrafts.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload.rows;
        state.draft.data = data.map((item: MovementItem) => item.attributes) || [];
        state.draft.totalRows = parseInt(action.payload.total_rows);
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        showError(action.payload || action.error.message);
      });
  },
});

export const { updateTableParams, updateSearchTerm } = movementSlice.actions;

export default movementSlice.reducer;
